import { Dispatch, SetStateAction, Suspense, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CircleNotch, MagnifyingGlass } from "@phosphor-icons/react"
import { debounce } from "es-toolkit/function"
import { FormError } from "@/components/ui/form"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { SearchClearButton, SuggestionSearch } from "@/components/ui/search"
import { useClickAway } from "@/hooks/useClickAway"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { chMedicationRelationsByDiagnosisCodeQuery$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationRelationsByDiagnosisCodeQuery.graphql"
import { ChMedicationSearchResultWithTemplate } from "@/modules/patients/detail/medication/components/ch-medication-search-result-with-templates"
import { ChMedicationSearchResultWithoutTemplate } from "@/modules/patients/detail/medication/components/ch-medication-search-result-without-templates"
import { useChMedicationSearchItemQueryProductQuery$data } from "@/modules/patients/detail/medication/services/__generated__/useChMedicationSearchItemQueryProductQuery.graphql"
import { useChMedicationSearchQuery$data } from "@/modules/patients/detail/medication/services/__generated__/useChMedicationSearchQuery.graphql"
import {
  handleMedicationSearch,
  handleMedicationSearchItem,
} from "@/modules/patients/detail/medication/services/useChMedicationSearchItemQuery"
import { MIN_MEDICATION_SEARCH_CHARACTERS } from "@/modules/patients/detail/medication/types/medication-config"
import { ChMedicationType, Language } from "@/types/__generated__/union-types"

type SearchResultStatus = "default" | "noResults" | "hasResults"
type MedicationData = useChMedicationSearchQuery$data["ch_medicationSearch"]["results"]
export type MedicationItem = useChMedicationSearchQuery$data["ch_medicationSearch"]["results"][number]
export type MedicationHighlight = MedicationItem["highlights"]
export type MedicationDetail = Partial<
  MedicationItem & useChMedicationSearchItemQueryProductQuery$data["ch_medicationProductSearchItem"]
>
export type ChMedicationSearchState = {
  isLoading: boolean
  searchResultStatus: SearchResultStatus
  data?: MedicationData
}

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  isLoading?: boolean
  error?: string
  testId?: string
  inputRef: React.RefObject<HTMLInputElement | null>
  clearSearchRef: React.RefObject<HTMLButtonElement | null>
  onClear: VoidFunction
}

const handleHighlightContent = (
  defaultValues: { name: string; mainSubstance?: string | null },
  highlights?: MedicationHighlight,
) => {
  const name = highlights?.find((h) => h.key === "name")?.value ?? defaultValues.name
  const substance = highlights?.find((h) => h.key === "mainSubstance")?.value ?? defaultValues.mainSubstance

  return { title: joinStrings([name, substance]), isHighlight: !!highlights }
}

const SearchInput = (props: SearchInputProps) => {
  const { className, isLoading, error, testId, id, inputRef, clearSearchRef, onChange, onClear, onClick, ...rest } =
    props

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (clearSearchRef.current) {
      clearSearchRef.current.style.display = event.currentTarget.value.trim().length > 0 ? "block" : "none"
    }
    onChange?.(event)
  }

  return (
    <>
      <div className="relative">
        <label htmlFor={id} className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
          {isLoading ? (
            <CircleNotch className="text-on-surface-subdued animate-spin" />
          ) : (
            <MagnifyingGlass className="text-on-surface-subdued" />
          )}
        </label>
        <input
          id={id}
          ref={inputRef}
          data-testid={`search-input-${testId}`}
          className={cn(
            "peer border-border text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed",
            error && "outline-critical! outline-1 focus:outline-2",
            className,
          )}
          autoComplete="off"
          aria-autocomplete="none"
          onChange={handleChange}
          onClick={onClick}
          onKeyDown={(event) => {
            const target = event.currentTarget
            if (event.key === "Home") {
              event.preventDefault()
              target.setSelectionRange(0, event.shiftKey ? target.selectionStart : 0)
            } else if (event.key === "End") {
              event.preventDefault()
              const range = target.value.length
              target.setSelectionRange(event.shiftKey ? target.selectionStart : range, range)
            }
          }}
          {...rest}
        />
        <SearchClearButton
          ref={clearSearchRef}
          data-testid="clear-search-btn"
          disabled={isLoading}
          style={{ display: "none" }}
          onClick={onClear}
        />
      </div>
      {error && <FormError>{error}</FormError>}
    </>
  )
}

type ChMedicationSearchProps = {
  existingMedications?: { title: string; options: chMedicationFragmentInline$data[] }[]
  includeTemplates: boolean
  patientLanguage: Language
  className?: string
  useMedicationIcon?: boolean
  usePopover?: boolean
  error?: string
  handleSelectMedication: (medication: MedicationDetail) => void
  handleFreeTextMedicationSelection: (inputValue: string) => void
  handleSelectExistingMedication: (item: chMedicationFragmentInline$data, medication?: MedicationDetail) => void
  medicationSearchText?: string | null
  setMedicationSearchText?: Dispatch<SetStateAction<string | null>>
  patientId: string
  appointmentId?: string | null
  medicationRelationsByDiagnosisCode?: chMedicationRelationsByDiagnosisCodeQuery$data["ch_medicationRelationsByDiagnosisCode"]
}

const DEFAULT_SEARCH_STATE: ChMedicationSearchState = { isLoading: false, searchResultStatus: "default" }

export const ChMedicationSearch = (props: ChMedicationSearchProps) => {
  const {
    existingMedications,
    className,
    includeTemplates,
    patientLanguage,
    useMedicationIcon,
    usePopover,
    error,
    handleSelectMedication,
    handleFreeTextMedicationSelection,
    handleSelectExistingMedication,
    medicationSearchText,
    setMedicationSearchText,
    patientId,
    appointmentId,
    medicationRelationsByDiagnosisCode,
  } = props

  const { t } = useTranslation()

  const [state, setState] = useState<ChMedicationSearchState>(DEFAULT_SEARCH_STATE)
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const clearSearchRef = useRef<HTMLButtonElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const previousSearchRef = useRef("")
  const searchResultsRef = useClickAway(() => {
    if (usePopover) setOpen(false)
    setState((prev) => ({ ...prev, searchResultStatus: "default" }))
  })
  const handleSearchInternal = async (value: string) => {
    previousSearchRef.current = value
    if (previousSearchRef.current.length < MIN_MEDICATION_SEARCH_CHARACTERS) {
      setState((prev) => {
        if (prev.data) return { ...prev, data: undefined, searchResultStatus: "default" }
        return prev
      })
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setState((prev) => ({ ...prev, isLoading: true, searchResultStatus: "default" }))

    const medicationData = await handleMedicationSearch(
      { searchTerm: previousSearchRef.current, includeTemplates, patientId, appointmentId },
      abortControllerRef.current.signal,
    )
    const filteredMedication = medicationData?.results.filter(
      (med) => med.pharmacode || med.medicationType === "SUBSTANCE",
    )

    let searchResultStatus: SearchResultStatus = "default"
    let data: MedicationData | undefined = undefined

    if (filteredMedication && filteredMedication.length > 0) {
      searchResultStatus = "hasResults"
      const templateMedications: MedicationItem[] = []
      const substanceMedications: MedicationItem[] = []
      const preparationMedications: MedicationItem[] = []

      filteredMedication.forEach((medication) => {
        if (medication.medicationType === "TEMPLATE") templateMedications.push(medication)
        else if (medication.medicationType === "SUBSTANCE") substanceMedications.push(medication)
        else if (medication.medicationType === "PREPARATION") preparationMedications.push(medication)
      })

      data = [...templateMedications, ...substanceMedications, ...preparationMedications]
    } else {
      searchResultStatus = "noResults"
    }

    setState({ isLoading: false, searchResultStatus, data })

    if (usePopover) setOpen(true)
  }

  const debounceSearch = useRef(debounce(handleSearchInternal, 500)).current

  const handleClear = () => {
    setState(DEFAULT_SEARCH_STATE)
    setOpen(false)
    if (inputRef.current) {
      inputRef.current.value = ""
    }
    if (clearSearchRef.current) {
      clearSearchRef.current.style.display = "none"

      // The snippet below is used to clear the input value in case the inputRef is not set inside the Popover
      if (!inputRef.current && clearSearchRef.current.parentElement) {
        const input = clearSearchRef.current.parentElement.querySelector("input")
        if (input) input.value = ""
      }
    }
    previousSearchRef.current = ""
  }

  const fetchMedicationDetail = async (
    medicationType: ChMedicationType,
    templateId?: number | null,
    pharmaCode?: number | null,
  ) => {
    if (medicationType === "PREPARATION" || medicationType === "TEMPLATE") {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      const medicationDetail = await handleMedicationSearchItem(
        abortControllerRef.current.signal,
        medicationType,
        patientLanguage,
        templateId,
        pharmaCode,
      )
      return medicationDetail
    }
  }

  const onSelectMedication = async (medication: Partial<MedicationItem>) => {
    const { itemId, medicationType, name, pharmacode } = medication
    if (medicationType) {
      const medicationDetail = await fetchMedicationDetail(medicationType, itemId, pharmacode)
      handleSelectMedication(medicationDetail ? { ...medicationDetail, name, itemId, pharmacode } : medication)
    }
    handleClear()
  }

  const onSelectExistingMedication = async (item: chMedicationFragmentInline$data) => {
    const { templateId, pharmaCode, type, name } = item
    const medicationDetail = await fetchMedicationDetail(type, templateId!, pharmaCode!)

    handleSelectExistingMedication(
      item,
      medicationDetail ? { ...medicationDetail, name, itemId: pharmaCode! } : undefined,
    )
    handleClear()
  }

  const handleClickOnInput = (event: React.MouseEvent<HTMLInputElement>) => {
    if (usePopover) {
      event.stopPropagation()
      setOpen(
        (!!existingMedications ||
          (!!medicationRelationsByDiagnosisCode && medicationRelationsByDiagnosisCode.length > 0)) &&
          state.searchResultStatus === "default",
      )
    } else {
      setState((prev) => {
        if (!prev.data) return prev
        return { ...prev, searchResultStatus: prev.data.length === 0 ? "noResults" : "hasResults" }
      })
    }
  }

  const handleSelectFreeTextSearch = () => {
    handleFreeTextMedicationSelection(previousSearchRef.current)
    handleClear()
  }

  useEffect(() => {
    // using setTimeout to set focus manually and prevent flicking
    if (!usePopover) {
      setTimeout(() => inputRef.current?.focus(), 150)
    }

    return () => abortControllerRef.current?.abort()
  }, [usePopover])

  useEffect(() => {
    if (medicationSearchText && inputRef.current && setMedicationSearchText) {
      inputRef.current.value = medicationSearchText
      debounceSearch(medicationSearchText)
      setMedicationSearchText(null)
    }
  }, [medicationSearchText, debounceSearch, setMedicationSearchText])

  if (usePopover) {
    return (
      <Popover open={open} onOpenChange={(open) => setState((prev) => ({ ...prev, open }))}>
        <PopoverTrigger>
          <SearchInput
            id="medication-search"
            inputRef={inputRef}
            clearSearchRef={clearSearchRef}
            placeholder={t("patient.medication.searchPlaceHolder")}
            isLoading={state.isLoading}
            onChange={(e) => debounceSearch(e.target.value)}
            error={error}
            onClear={handleClear}
            onClick={handleClickOnInput}
            testId="medication"
          />
        </PopoverTrigger>
        <PopoverContent
          className="bg-surface pointer-events-auto z-9999 -mt-0.75 max-h-87.5 w-(--radix-popover-trigger-width) overflow-y-auto rounded-md p-2"
          side="bottom"
          align="start"
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <Suspense fallback={null}>
            <ChMedicationSearchResultWithTemplate
              existingMedications={existingMedications}
              useMedicationIcon={useMedicationIcon}
              searchResultsRef={searchResultsRef}
              searchValue={previousSearchRef.current}
              handleSelectFreeTextSearch={handleSelectFreeTextSearch}
              handleSelectMedication={onSelectMedication}
              handleSelectExistingMedication={onSelectExistingMedication}
              handleHighlightContent={handleHighlightContent}
              medicationRelationsByDiagnosisCode={medicationRelationsByDiagnosisCode}
              {...state}
            />
          </Suspense>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <SuggestionSearch className={className}>
      <SearchInput
        id="medication-search"
        inputRef={inputRef}
        clearSearchRef={clearSearchRef}
        placeholder={t("patient.medication.searchPlaceHolder")}
        isLoading={state.isLoading}
        onChange={(e) => debounceSearch(e.target.value)}
        error={error}
        onClear={handleClear}
        onClick={handleClickOnInput}
        testId="medication"
      />

      {includeTemplates ? (
        <Suspense fallback={null}>
          <ChMedicationSearchResultWithTemplate
            existingMedications={existingMedications}
            useMedicationIcon={useMedicationIcon}
            searchResultsRef={searchResultsRef}
            searchValue={previousSearchRef.current}
            handleSelectFreeTextSearch={handleSelectFreeTextSearch}
            handleSelectMedication={onSelectMedication}
            handleSelectExistingMedication={onSelectExistingMedication}
            handleHighlightContent={handleHighlightContent}
            medicationRelationsByDiagnosisCode={medicationRelationsByDiagnosisCode}
            {...state}
          />
        </Suspense>
      ) : (
        <ChMedicationSearchResultWithoutTemplate
          handleSelectFreeTextSearch={handleSelectFreeTextSearch}
          handleSelectMedication={onSelectMedication}
          handleHighlightContent={handleHighlightContent}
          searchResultsRef={searchResultsRef}
          searchValue={previousSearchRef.current}
          {...state}
        />
      )}
    </SuggestionSearch>
  )
}
